import React, { useEffect, useRef, useState } from "react";
import { useFormWrapper, useStoreState } from "../../util/util";
import { PrimaryButtonSmall } from "../Buttons/Buttons";
import styled from "styled-components/macro";
import { SendIcon, StopIcon } from "../Icons/Icons";
import { TextArea } from "../TextArea/TextArea";
import axios from "axios";

const MessageInput = styled.div`
  flex-grow: 1;
`;

const ButtonContainer = styled.div`
  display: flex;
  flex-direction: column;
  justify-content: flex-end;
  margin-left: ${({ theme }) => theme.resolve("spacing_lg")};
`;

type RunStatus =
  | "queued"
  | "in_progress"
  | "completed"
  | "cancelled"
  | "failed"
  | "incomplete";

export function SendMessageToChatBot({
  handleMessageInProgress,
  handleSetThreadId,
  fetchLastMessageData,
  inProgress,
  threadId,
  activeRunId,
  resetChat,
  addErrorMessage,
}: {
  handleMessageInProgress: (inProgress: boolean, message?: string) => void;
  handleSetThreadId: (id: string | null) => void;
  fetchLastMessageData: (threadId: string, runId: string) => Promise<void>;
  inProgress: boolean;
  threadId: string | null;
  activeRunId?: string | null;
  resetChat: () => void;
  addErrorMessage: (message?: string) => void;
}) {
  const { register, formState, handleSubmit, setValue, getValues, watch } =
    useFormWrapper({});
  const { storefront_id } = useStoreState();
  const [inputHeight, setInputHeight] = useState(20);
  const [newMessageText, setNewMessageText] = useState("");
  const [currentRunId, setCurrentRunId] = useState<string | null>(null);
  const inputEl = useRef<HTMLTextAreaElement | null>(null);
  const timer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const generation = useRef(0);
  const mounted = useRef(true);
  const busy = useRef(false);
  const callbacks = useRef({
    handleMessageInProgress,
    fetchLastMessageData,
    addErrorMessage,
  });
  callbacks.current = {
    handleMessageInProgress,
    fetchLastMessageData,
    addErrorMessage,
  };

  const stopPolling = () => {
    generation.current++;
    if (timer.current) clearTimeout(timer.current);
    timer.current = null;
  };
  useEffect(
    () => () => {
      mounted.current = false;
      stopPolling();
    },
    []
  );

  const messageInputChange = (e: React.FormEvent<HTMLTextAreaElement>) => {
    setNewMessageText(e.currentTarget.value);
    setValue("message", e.currentTarget.value);
  };

  const pollRunStatus = (chatId: string, runId: string) => {
    stopPolling();
    const token = generation.current;
    const started = Date.now();
    busy.current = true;
    setCurrentRunId(runId);
    callbacks.current.handleMessageInProgress(true);
    const finish = () => {
      busy.current = false;
      setCurrentRunId(null);
      callbacks.current.handleMessageInProgress(false);
    };
    const poll = async () => {
      try {
        const { data } = await axios.get<{ status: RunStatus }>(
          `/v2/storefronts/${storefront_id}/chatbots/threads/${chatId}/runs/${runId}`
        );
        if (!mounted.current || token !== generation.current) return;
        if (data.status === "queued" || data.status === "in_progress") {
          if (Date.now() - started > 5 * 60 * 1000) {
            callbacks.current.addErrorMessage(
              "Generation is taking longer than expected. Reload to resume checking or stop it."
            );
            // Keep the response ID and Stop button: the server may still be running.
            return;
          }
          timer.current = setTimeout(poll, 1500);
          return;
        }
        if (data.status === "completed" || data.status === "incomplete") {
          await callbacks.current.fetchLastMessageData(chatId, runId);
          if (!mounted.current || token !== generation.current) return;
        }
        if (data.status === "incomplete" || data.status === "failed") {
          callbacks.current.addErrorMessage(
            "The answer could not be completed. Please try again."
          );
        }
        finish();
      } catch (error) {
        if (!mounted.current || token !== generation.current) return;
        callbacks.current.addErrorMessage(
          "Unable to check the answer. Reload to resume or try stopping it."
        );
        // Preserve Stop/recovery instead of allowing a second concurrent request.
      }
    };
    void poll();
  };

  useEffect(() => {
    if (threadId && activeRunId) pollRunStatus(threadId, activeRunId);
    // Resume the server's active response after a page reload.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [threadId, activeRunId]);

  const onCancel = async () => {
    if (!threadId || !currentRunId) return;
    const runId = currentRunId;
    stopPolling();
    try {
      const { data } = await axios.post<{ status: RunStatus }>(
        `/v2/storefronts/${storefront_id}/chatbots/threads/${threadId}/runs/${runId}/cancel`
      );
      if (!mounted.current) return;
      if (data.status === "completed") {
        await fetchLastMessageData(threadId, runId);
      }
      if (!mounted.current) return;
      busy.current = false;
      handleMessageInProgress(false);
      setCurrentRunId(null);
    } catch (error) {
      if (!mounted.current) return;
      addErrorMessage("There was an error cancelling the message generation");
      pollRunStatus(threadId, runId);
    }
  };

  const onSubmit = async (data: { message: string }) => {
    const text = data.message?.trim();
    if (!text || busy.current || inProgress) return;
    busy.current = true;
    handleMessageInProgress(true, text);
    setValue("message", "");
    setNewMessageText("");
    setInputHeight(20);
    try {
      const response = threadId
        ? await axios.post(
            `/v2/storefronts/${storefront_id}/chatbots/threads/${threadId}/messages`,
            { content: text }
          )
        : await axios.post(
            `/v2/storefronts/${storefront_id}/chatbots/threads`,
            {
              messages: [{ content: text, role: "user" }],
            }
          );
      if (!mounted.current) return;
      const chatId = threadId || response.data.thread_id;
      if (!chatId || !response.data.run_id) {
        throw new Error("Invalid chat response");
      }
      handleSetThreadId(chatId);
      pollRunStatus(chatId, response.data.run_id);
    } catch (error) {
      if (!mounted.current) return;
      busy.current = false;
      handleMessageInProgress(false);
      if (
        axios.isAxiosError(error) &&
        [404, 410].includes(error.response?.status || 0)
      ) {
        resetChat();
      }
      addErrorMessage("There was an error sending your message. Please retry.");
      setNewMessageText(text);
      setValue("message", text);
    }
  };

  useEffect(() => {
    const handleKeyPress = (e: KeyboardEvent): void => {
      // prevent sending empty message with newline when user press enter and there's no text
      if (
        document.activeElement === inputEl.current &&
        e.key === "Enter" &&
        !e.shiftKey &&
        inputEl.current?.value === ""
      ) {
        e.preventDefault();
        setNewMessageText("");
      }
      if (
        document.activeElement === inputEl.current &&
        e.key === "Enter" &&
        !e.shiftKey &&
        inProgress
      ) {
        e.preventDefault();
      }

      // send new message when user press Enter key and the focus is on the Input and there's an input value
      if (
        document.activeElement === inputEl.current &&
        inputEl.current?.value &&
        !inProgress
      ) {
        inputEl.current &&
          setInputHeight(
            inputEl.current?.scrollHeight < 110
              ? inputEl.current?.scrollHeight
              : 115
          );
        if (e.key === "Enter" && !e.shiftKey) {
          if (inputEl.current?.value !== "") {
            e.preventDefault();
            onSubmit({ message: getValues("message") });
          }
        }
        if (e.key === "Enter" && e.shiftKey) {
          setInputHeight(
            inputEl.current?.scrollHeight < 110
              ? inputEl.current?.scrollHeight
              : 115
          );
        }
      }
    };
    if (inputEl.current) {
      window.addEventListener("keydown", handleKeyPress, false);
    }
    return () => {
      window.removeEventListener("keydown", handleKeyPress, false);
    };
  });

  useEffect(() => {
    register({ name: "message", required: true });
  }, [register]);

  const message = watch("message");
  const hasMessage = Boolean(message?.trim());

  return (
    <>
      <MessageInput>
        <form
          noValidate
          onSubmit={handleSubmit(onSubmit)}
          style={{
            position: "relative",
            display: "flex",
            width: "100%",
            alignItems: "center",
          }}
        >
          <TextArea
            name={"message"}
            label={"Write a message..."}
            theref={inputEl}
            min={1}
            formState={formState}
            onChange={messageInputChange}
            autoHeight={true}
            required={true}
            style={{
              height: `${inputHeight}px`,
              resize: "none",
              width: "100%",
            }}
            rows={1}
            value={newMessageText}
          />

          <ButtonContainer>
            {inProgress ? (
              <PrimaryButtonSmall
                style={{
                  width: "51px",
                  height: "51px",
                  padding: "0",
                  display: "flex",
                  justifyContent: "center",
                  alignItems: "center",
                }}
                onClick={onCancel}
                type="button"
                disabled={!currentRunId}
              >
                <StopIcon width={20} height={20} />
              </PrimaryButtonSmall>
            ) : (
              <PrimaryButtonSmall
                style={{
                  width: "51px",
                  height: "51px",
                  padding: "0",
                  display: "flex",
                  justifyContent: "center",
                  alignItems: "center",
                }}
                onClick={handleSubmit(onSubmit)}
                disabled={!hasMessage}
              >
                <SendIcon />
              </PrimaryButtonSmall>
            )}
          </ButtonContainer>
        </form>
      </MessageInput>
    </>
  );
}
